import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet";
import Select from "react-select";
import * as XLSX from "xlsx";
import withTokenCheck from "../../withTokenCheck";

const emptyForm = {
    funcionario: "",
    centro_de_costos: "",
    contacto: "",
    fecha_del_servicio: "",
    hora_del_servicio: "",
    tipo_de_servicio: "",
    zona: "",
    direccion_origen: "",
    direccion_destino: "",
    observaciones: "",
    cantidad_pax: "",
    tercero: false,
    proveedor: "",
    direccion_origen_homologacion_id: "",
};

const columns = [
    { key: "id", label: "ID" },
    { key: "funcionario", label: "Funcionario" },
    { key: "centro_de_costos", label: "Centro de costos" },
    { key: "contacto", label: "Contacto" },
    { key: "fecha_del_servicio", label: "Fecha servicio" },
    { key: "hora_del_servicio", label: "Hora servicio" },
    { key: "tipo_de_servicio", label: "Tipo de servicio" },
    { key: "zona", label: "Zona" },
    { key: "direccion_origen", label: "Direccion origen" },
    { key: "codigo_relacionado", label: "Codigo relacionado" },
    { key: "direccion_destino", label: "Direccion destino" },
    { key: "observaciones", label: "Observaciones" },
    { key: "cantidad_pax", label: "PAX" },
    { key: "tercero", label: "Tercero" },
    { key: "link", label: "Link" },
    { key: "proveedor", label: "Proveedor" },
    { key: "direccion_origen_homologacion_id", label: "Homologacion ID" },
    { key: "created_by", label: "Created by" },
    { key: "created_by_nombre", label: "Usuario creacion" },
    { key: "created_at", label: "Created at" },
    { key: "updated_by", label: "Updated by" },
    { key: "updated_by_nombre", label: "Usuario actualizacion" },
    { key: "updated_at", label: "Updated at" },
    { key: "deleted_by", label: "Deleted by" },
    { key: "deleted_by_nombre", label: "Usuario eliminacion" },
    { key: "deleted_at", label: "Deleted at" },
];

const getColumnValue = (row, key) => {
    if (key === "tercero") return row.tercero ? "Si" : "No";
    if (key === "codigo_relacionado") {
        return row.direccion_origen_homologacion?.origen?.codigo || row.direccion_origen_homologacion?.codigo_relacionado || row.direccion_origen_homologacion?.CodigoRelacionado || "";
    }
    if (key === "link") return row.direccion_origen_homologacion?.origen?.link || row.direccion_origen_homologacion?.link || "";
    if (key === "created_by") return row.created_by?.id || row.created_by || "";
    if (key === "created_by_nombre") return row.created_by?.name || "";
    if (key === "updated_by") return row.updated_by?.id || row.updated_by || "";
    if (key === "updated_by_nombre") return row.updated_by?.name || "";
    if (key === "deleted_by") return row.deleted_by?.id || row.deleted_by || "";
    if (key === "deleted_by_nombre") return row.deleted_by?.name || "";
    return row[key] ?? "";
};

const buildExcelRows = (rows) =>
    rows.map((row) =>
        columns.reduce((item, column) => {
            item[column.label] = getColumnValue(row, column.key);
            return item;
        }, {})
    );

const SolicitudesLogistica = ({ token }) => {
    const [rows, setRows] = useState([]);
    const [homologaciones, setHomologaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sortBy, setSortBy] = useState("id");
    const [sortDir, setSortDir] = useState("desc");
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        from: 0,
        to: 0,
    });

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchRows = useCallback(() => {
        if (!token) return;
        setLoading(true);
        axios
            .get("/solicitudesLogistica", {
                headers,
                params: { page, per_page: perPage, search, sort_by: sortBy, sort_dir: sortDir },
            })
            .then((response) => {
                setRows(response.data.data || []);
                setPagination({
                    current_page: response.data.current_page || 1,
                    last_page: response.data.last_page || 1,
                    total: response.data.total || 0,
                    from: response.data.from || 0,
                    to: response.data.to || 0,
                });
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [headers, page, perPage, search, sortBy, sortDir, token]);

    const fetchHomologaciones = useCallback(() => {
        if (!token) return;
        axios
            .get("/direccionOrigenHomologacion", { headers })
            .then((response) => setHomologaciones(response.data))
            .catch(() => setHomologaciones([]));
    }, [headers, token]);

    useEffect(fetchHomologaciones, [fetchHomologaciones]);
    useEffect(fetchRows, [fetchRows]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setPage(1);
            setSearch(searchInput.trim());
        }, 450);

        return () => clearTimeout(timeout);
    }, [searchInput]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            ...emptyForm,
            ...row,
            cantidad_pax: row.cantidad_pax ?? "",
            direccion_origen_homologacion_id: row.direccion_origen_homologacion_id || "",
        });
        setModalOpen(true);
    };

    const save = (event) => {
        event.preventDefault();
        const payload = {
            ...form,
            cantidad_pax: form.cantidad_pax === "" ? null : parseInt(form.cantidad_pax, 10),
            direccion_origen_homologacion_id: form.direccion_origen_homologacion_id || null,
        };
        const request = editing
            ? axios.put(`/solicitudesLogistica/${editing.id}`, payload, { headers })
            : axios.post("/solicitudesLogistica", payload, { headers });

        request
            .then(() => {
                Swal.fire("Guardado", "La solicitud fue guardada.", "success").then(() => {
                    setModalOpen(false);
                    fetchRows();
                });
            })
            .catch((error) => {
                const message = error.response?.data?.message || "No fue posible guardar la solicitud.";
                Swal.fire("Error", message, "error");
            });
    };

    const remove = (row) => {
        axios
            .delete(`/solicitudesLogistica/${row.id}`, { headers })
            .then(() => Swal.fire("Eliminado", "La solicitud fue eliminada.", "success").then(fetchRows))
            .catch(() => Swal.fire("Error", "No fue posible eliminar la solicitud.", "error"));
    };

    const exportExcel = () => {
        setExporting(true);
        axios
            .get("/solicitudesLogistica", {
                headers,
                params: { all: 1, search, sort_by: sortBy, sort_dir: sortDir },
            })
            .then((response) => {
                const worksheet = XLSX.utils.json_to_sheet(buildExcelRows(response.data || []));
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Solicitudes");
                XLSX.writeFile(workbook, "solicitudes_logistica.xlsx");
            })
            .catch(() => Swal.fire("Error", "No fue posible descargar el Excel.", "error"))
            .finally(() => setExporting(false));
    };

    const sortRowsBy = (columnKey) => {
        setPage(1);
        if (sortBy === columnKey) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
            return;
        }

        setSortBy(columnKey);
        setSortDir("asc");
    };

    const getSortIndicator = (columnKey) => {
        if (sortBy !== columnKey) return "";
        return sortDir === "asc" ? " ▲" : " ▼";
    };

    const setField = (field, value) => setForm({ ...form, [field]: value });
    const isTercero = (cantidadPax) => parseInt(cantidadPax || 0, 10) > 1;
    const homologacionOptions = homologaciones.map((item) => ({
        value: item.id,
        label: `${item.origen?.codigo || item.CodigoRelacionado || "Sin codigo"} - ${item.Direccion}`,
    }));
    const selectedHomologacion =
        homologacionOptions.find((option) => String(option.value) === String(form.direccion_origen_homologacion_id)) || null;

    return (
        <>
            <Helmet>
                <title>Solicitudes Logistica</title>
            </Helmet>
            <div style={{ maxWidth: "1600px", margin: "50px auto" }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="d-flex gap-2 flex-wrap">
                        <Button color="primary" onClick={openCreate}>Crear solicitud</Button>
                        <Button color="success" onClick={exportExcel} disabled={exporting}>
                            {exporting ? "Descargando..." : "Excel"}
                        </Button>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <label className="mb-0">Buscar:</label>
                        <input
                            className="form-control"
                            style={{ width: 280 }}
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                        <label className="mb-0">Registros:</label>
                        <select
                            className="form-control"
                            style={{ width: 90 }}
                            value={perPage}
                            onChange={(event) => {
                                setPage(1);
                                setPerPage(parseInt(event.target.value, 10));
                            }}
                        >
                            {[10, 25, 50, 100, 250, 500].map((value) => (
                                <option key={value} value={value}>{value}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {loading ? (
                    <div>Cargando...</div>
                ) : (
                    <>
                        <div style={{ width: "100%", overflowX: "auto" }}>
                            <table className="table table-striped table-bordered table-sm" style={{ minWidth: "2200px" }}>
                                <thead>
                                    <tr>
                                        <th>Editar</th>
                                        <th>Eliminar</th>
                                        {columns.map((column) => (
                                            <th key={column.key}>
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0 text-decoration-none text-dark fw-semibold"
                                                    onClick={() => sortRowsBy(column.key)}
                                                >
                                                    {column.label}{getSortIndicator(column.key)}
                                                </button>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length ? (
                                        rows.map((row) => (
                                            <tr key={row.id}>
                                                <td><Button color="secondary" size="sm" onClick={() => openEdit(row)}>Editar</Button></td>
                                                <td><Button color="danger" size="sm" onClick={() => remove(row)}>Eliminar</Button></td>
                                                {columns.map((column) => (
                                                    <td key={column.key}>{getColumnValue(row, column.key)}</td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={columns.length + 2} className="text-center">Sin registros</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
                            <div>
                                Mostrando {pagination.from || 0} a {pagination.to || 0} de {pagination.total} registros.
                                Pagina {pagination.current_page} de {pagination.last_page}.
                            </div>
                            <div className="btn-group">
                                <Button color="secondary" disabled={page <= 1} onClick={() => setPage(1)}>Primera</Button>
                                <Button color="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                                <Button color="secondary" disabled={page >= pagination.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
                                <Button color="secondary" disabled={page >= pagination.last_page} onClick={() => setPage(pagination.last_page)}>Ultima</Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <Modal size="lg" isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
                <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    {editing ? "Editar solicitud" : "Crear solicitud"}
                </ModalHeader>
                <ModalBody>
                    <div className="row">
                        {[
                            ["funcionario", "Funcionario"],
                            ["centro_de_costos", "Centro de costos"],
                            ["contacto", "Contacto"],
                            ["fecha_del_servicio", "Fecha del servicio", "date"],
                            ["hora_del_servicio", "Hora del servicio", "time"],
                            ["tipo_de_servicio", "Tipo de servicio"],
                            ["zona", "Zona"],
                            ["cantidad_pax", "Cantidad PAX", "number"],
                            ["proveedor", "Proveedor"],
                        ].map(([field, label, type = "text"]) => (
                            <div className="col-md-6 mb-3" key={field}>
                                <label>{label}</label>
                                <input className="form-control" type={type} value={form[field] || ""} onChange={(event) => setField(field, event.target.value)} />
                            </div>
                        ))}
                        <div className="col-md-6 mb-3">
                            <label>Tercero</label>
                            <input className="form-control" value={isTercero(form.cantidad_pax) ? "Si" : "No"} readOnly />
                        </div>
                        <div className="col-md-12 mb-3">
                            <label>Direccion origen</label>
                            <textarea className="form-control" value={form.direccion_origen || ""} onChange={(event) => setField("direccion_origen", event.target.value)} />
                        </div>
                        <div className="col-md-12 mb-3">
                            <label>Homologacion direccion origen</label>
                            <Select
                                isClearable
                                placeholder="Resolver automaticamente por direccion"
                                noOptionsMessage={() => "Sin resultados"}
                                options={homologacionOptions}
                                value={selectedHomologacion}
                                onChange={(option) => setField("direccion_origen_homologacion_id", option?.value || "")}
                            />
                        </div>
                        <div className="col-md-12 mb-3">
                            <label>Direccion destino</label>
                            <textarea className="form-control" value={form.direccion_destino || ""} onChange={(event) => setField("direccion_destino", event.target.value)} />
                        </div>
                        <div className="col-md-12">
                            <label>Observaciones</label>
                            <textarea className="form-control" value={form.observaciones || ""} onChange={(event) => setField("observaciones", event.target.value)} />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={save}>Guardar</Button>
                    <Button color="danger" onClick={() => setModalOpen(false)}>Cancelar</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default withTokenCheck(SolicitudesLogistica);
